import { useEffect, useState } from "react";
import { Building2, IdCard, Inbox, Eye, UserPlus, Nfc, QrCode } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import { StatCard, Spinner } from "@/components/dashboard/DashBits";
import { MetricsPanel } from "@/components/dashboard/MetricsPanel";

export default function Overview() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isAdmin = user.role === "superadmin";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(isAdmin ? "/admin/overview" : "/company/overview").then(({ data }) => setData(data)).catch(() => setData(false));
  }, [isAdmin]);

  if (data === null) return <Spinner />;
  if (data === false) return <p className="text-zinc-400">{t("dash.loadError")}</p>;

  const totals = data.stats?.totals || {};

  return (
    <div>
      <h1 className="font-display font-bold text-3xl text-white">{t("dash.hello")} {user.name} 👋</h1>
      <p className="mt-1 text-zinc-400">{isAdmin ? t("dash.overviewAdmin") : t("dash.overviewClient", { name: data.company?.name || "" })}</p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        {isAdmin ? (
          <>
            <StatCard icon={Building2} label={t("dash.st_companies")} value={data.companies} testid="stat-companies" />
            <StatCard icon={IdCard} label={t("dash.st_activeProfiles")} value={`${data.active_profiles}/${data.profiles}`} accent="#8b5cf6" testid="stat-profiles" />
            <StatCard icon={Inbox} label={t("dash.st_unreadReq")} value={data.unread_requests} accent="#f59e0b" testid="stat-requests" />
            <StatCard icon={Eye} label={t("dash.st_views30")} value={totals.views || 0} accent="#3b82f6" testid="stat-views" />
          </>
        ) : (
          <>
            <StatCard icon={IdCard} label={t("dash.st_profiles")} value={`${data.usage.active}/${data.usage.purchased}`} sub={`${data.usage.available} ${t("dash.st_available")}`} testid="stat-profiles" />
            <StatCard icon={Eye} label={t("dash.st_views30")} value={totals.views || 0} accent="#3b82f6" testid="stat-views" />
            <StatCard icon={UserPlus} label={t("dash.st_adds")} value={totals.add_contact || 0} accent="#8b5cf6" testid="stat-adds" />
            <StatCard icon={Inbox} label={t("dash.st_unreadReq")} value={data.unread_requests} accent="#f59e0b" testid="stat-requests" />
          </>
        )}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[#0d0d14] p-6 lg:col-span-2">
          <h3 className="font-display font-semibold text-white">{t("dash.sources30")}</h3>
          <div className="mt-5 grid grid-cols-3 gap-4">
            {[[t("dash.nfc"), totals.nfc || 0, Nfc, "#06b6d4"], [t("dash.qr"), totals.qr || 0, QrCode, "#8b5cf6"], [t("dash.direct"), totals.direct || 0, Eye, "#3b82f6"]].map(([l, v, I, c]) => (
              <div key={l} className="rounded-xl bg-white/5 p-4">
                <I className="w-5 h-5" style={{ color: c }} />
                <p className="mt-3 font-display font-bold text-2xl text-white">{v}</p>
                <p className="text-xs text-zinc-500">{l}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#0d0d14] p-6">
          <h3 className="font-display font-semibold text-white">{t("dash.recentReq")}</h3>
          <div className="mt-4 space-y-3">
            {(data.recent_requests || []).length === 0 && <p className="text-sm text-zinc-500">{t("dash.noReq")}</p>}
            {(data.recent_requests || []).map((r) => (
              <div key={r.id} className="text-sm">
                <p className="text-white">{r.first_name} {r.last_name}</p>
                <p className="text-zinc-500 text-xs">{r.email}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isAdmin && <MetricsPanel />}
    </div>
  );
}
